// reviewerApi.ts — API utilities for the reviewer/community review system

const API_ROOT =
  (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_URL) || '';

const normalisedRoot = API_ROOT ? API_ROOT.replace(/\/$/, '') : '';

/** Base URL for reviewer API endpoints */
export const REVIEWER_API_BASE = normalisedRoot
  ? `${normalisedRoot}/api`
  : '/api';

const isAbsoluteBase =
  REVIEWER_API_BASE.startsWith('http://') ||
  REVIEWER_API_BASE.startsWith('https://');

/** Build a reviewer API URL (relative or absolute depending on config) */
export const reviewerApiUrl = (path: string = ''): string => {
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  return `${REVIEWER_API_BASE}${normalisedPath}`;
};

/** Resolve to a fully-qualified URL (safe for fetch in both SSR and browser) */
export const resolveReviewerApiUrl = (path: string = ''): string => {
  const url = reviewerApiUrl(path);
  if (isAbsoluteBase || typeof window === 'undefined') {
    return url;
  }
  return new URL(url, window.location.origin).toString();
};

// ── Token management ────────────────────────────────────────

const TOKEN_KEY = 'reviewerToken';
const PENDING_REVIEW_KEY = 'pendingReview';
const PENDING_PHOTOS_KEY = 'pendingPhotos';

export const getReviewerToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
};

export const setReviewerToken = (token: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TOKEN_KEY, token);
};

export const clearReviewerToken = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
};

// ── Pending submission persistence (for progressive auth) ───

export const savePendingReview = (data: {
  entityId: number;
  entityName: string;
  formData: Record<string, unknown>;
}): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PENDING_REVIEW_KEY, JSON.stringify(data));
};

export const getPendingReview = (): {
  entityId: number;
  entityName: string;
  formData: Record<string, unknown>;
} | null => {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_REVIEW_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearPendingReview = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PENDING_REVIEW_KEY);
};

export const savePendingPhotos = (data: {
  entityId: number;
  captions: string[];
}): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PENDING_PHOTOS_KEY, JSON.stringify(data));
};

export const getPendingPhotos = (): {
  entityId: number;
  captions: string[];
} | null => {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_PHOTOS_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearPendingPhotos = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PENDING_PHOTOS_KEY);
};

// ── Authenticated fetch ─────────────────────────────────────

/** fetch() wrapper that attaches the reviewer Bearer token */
export const fetchWithReviewerAuth = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getReviewerToken();
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(url, { ...options, headers });
};

// ── OAuth helpers ───────────────────────────────────────────

export type ReviewerAuthProvider = 'google' | 'facebook' | 'shopify';

/**
 * Kick off the OAuth flow for reviewer authentication.
 * The backend returns an auth URL which we redirect the browser to.
 */
export const beginReviewerOAuth = async (
  provider: ReviewerAuthProvider,
  returnUrl?: string
): Promise<void> => {
  if (typeof window === 'undefined') {
    throw new Error('OAuth sign-in can only run in the browser.');
  }

  const currentUrl = window.location.href;
  const desiredReturn = returnUrl || currentUrl;

  const endpoint = resolveReviewerApiUrl(`/reviewer/auth/${provider}/login`);
  const loginUrl = new URL(endpoint);
  loginUrl.searchParams.set('return_url', desiredReturn);

  const response = await fetch(loginUrl.toString(), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  const payload = await response.json().catch(() => ({
    success: false,
    error: 'Unable to parse authentication response.',
  }));

  if (!response.ok || !payload.success || !payload.auth_url) {
    throw new Error(
      payload.error || 'Failed to start sign-in. Please try again.'
    );
  }

  // Store OAuth state so we can resume after redirect
  sessionStorage.setItem('reviewer_oauth_provider', provider);
  sessionStorage.setItem('reviewer_oauth_return', desiredReturn);

  window.location.href = payload.auth_url;
};

/**
 * Create an anonymous reviewer and return a short-lived JWT token.
 * No OAuth redirect needed — the token is returned immediately.
 */
export const authenticateAnonymous = async (): Promise<{
  token: string;
  reviewer: Record<string, unknown>;
}> => {
  const endpoint = resolveReviewerApiUrl('/reviewer/auth/anonymous');
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error || 'Failed to create anonymous session');
  }

  return { token: data.token, reviewer: data.reviewer };
};

// ── Public API helpers ──────────────────────────────────────

/** Safely parse JSON — returns null instead of throwing on empty / invalid body */
const safeJson = async (res: Response) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/** Fetch approved reviews for an entity (public, no auth) */
export const fetchEntityReviews = async (
  entityId: number,
  page = 1,
  perPage = 10,
  sort = 'recent'
) => {
  const url = resolveReviewerApiUrl(
    `/entities/${entityId}/reviews?page=${page}&per_page=${perPage}&sort=${sort}`
  );
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch reviews');
  return (await safeJson(res)) ?? { reviews: [], total: 0, pages: 0 };
};

/** Fetch review summary/aggregates for an entity (public) */
export const fetchReviewSummary = async (entityId: number) => {
  const url = resolveReviewerApiUrl(`/entities/${entityId}/review-summary`);
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch review summary');
  return (await safeJson(res)) ?? { summary: null };
};

/** Fetch AI-generated review summary for an entity (public, cached) */
export const fetchAIReviewSummary = async (entityId: number) => {
  try {
    const url = resolveReviewerApiUrl(`/entities/${entityId}/review-ai-summary`);
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await safeJson(res)) ?? null;
  } catch {
    // AI summary is optional — don't break the page if it fails
    return null;
  }
};

/** Fetch approved community images for an entity (public) */
export const fetchCommunityImages = async (
  entityId: number,
  page = 1,
  perPage = 20
) => {
  const url = resolveReviewerApiUrl(
    `/entities/${entityId}/community-images?page=${page}&per_page=${perPage}`
  );
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch community images');
  return (await safeJson(res)) ?? { images: [], total: 0 };
};

// ── Authenticated API helpers ───────────────────────────────

/** Submit a new review (requires auth) */
export const submitReview = async (
  entityId: number,
  data: Record<string, unknown>
) => {
  const url = resolveReviewerApiUrl(`/entities/${entityId}/reviews`);
  const res = await fetchWithReviewerAuth(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Submit failed' }));
    throw new Error(err.error || 'Failed to submit review');
  }
  return res.json();
};

/** Toggle "helpful" vote on a review */
export const toggleHelpful = async (reviewId: string) => {
  const url = resolveReviewerApiUrl(`/reviews/${reviewId}/helpful`);
  const res = await fetchWithReviewerAuth(url, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to toggle helpful');
  return res.json();
};

/** Get a presigned S3 URL for uploading a review image */
export const getReviewImageUploadUrl = async (
  reviewId: string,
  filename: string,
  contentType: string
) => {
  const url = resolveReviewerApiUrl(`/reviews/${reviewId}/images/upload-url`);
  const res = await fetchWithReviewerAuth(url, {
    method: 'POST',
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  if (!res.ok) throw new Error('Failed to get upload URL');
  return res.json();
};

/** Register a review image in the database after it has been uploaded to S3 */
export const registerReviewImage = async (
  reviewId: string,
  imageData: {
    image_src: string;
    sort_order?: number;
    content_type?: string;
    file_size?: number;
  }
) => {
  const url = resolveReviewerApiUrl(`/reviews/${reviewId}/images`);
  const res = await fetchWithReviewerAuth(url, {
    method: 'POST',
    body: JSON.stringify(imageData),
  });
  if (!res.ok) throw new Error('Failed to register review image');
  return res.json();
};

/** Get a presigned S3 URL for uploading a community photo */
export const getCommunityImageUploadUrl = async (
  entityId: number,
  filename: string,
  contentType: string
) => {
  const url = resolveReviewerApiUrl(
    `/entities/${entityId}/community-images/upload-url`
  );
  const res = await fetchWithReviewerAuth(url, {
    method: 'POST',
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  if (!res.ok) throw new Error('Failed to get upload URL');
  return res.json();
};

/** Register an uploaded community image in the database */
export const registerCommunityImage = async (
  entityId: number,
  imageData: {
    image_src: string;
    caption?: string;
    content_type?: string;
    file_size?: number;
  }
) => {
  const url = resolveReviewerApiUrl(`/entities/${entityId}/community-images`);
  const res = await fetchWithReviewerAuth(url, {
    method: 'POST',
    body: JSON.stringify(imageData),
  });
  if (!res.ok) throw new Error('Failed to register image');
  return res.json();
};

/** Get current reviewer profile */
export const fetchReviewerProfile = async () => {
  const url = resolveReviewerApiUrl('/reviewer/me');
  const res = await fetchWithReviewerAuth(url);
  if (!res.ok) return null;
  return res.json();
};
